import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Lock, Brain, Heart, ArrowRight, ArrowLeft, Check, Sparkles, Calendar, TrendingUp, LogOut,
} from 'lucide-react';
import { API, useAuth } from '../../App';
import QMGSymbols from '../../components/QMGSymbols';

const FEATURES = [
  'Daily Awareness logging (Quick + Detailed modes)',
  'Track Acceptance & Action KPIs as they roll out',
  'Trend charts that show how your mind shows up over time',
  'Round notes, course-specific reflection, "the gap" metric',
  'Coach-ready summaries when you choose to share',
];

const LockerPaywallPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'trial' | 'checkout' | null
  const cancelled = new URLSearchParams(location.search).get('cancelled') === 'true';

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get('/locker/access');
        setAccess(data);
        // Already has access? bounce them in.
        if (data.has_access) {
          navigate('/locker', { replace: true });
        }
      } catch (err) {
        setAccess({ has_access: false, trial_used: false });
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const startTrial = async () => {
    setBusy('trial');
    try {
      const { data } = await API.post('/locker/trial/start');
      toast.success(data.message || 'Trial started');
      navigate('/locker', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to start trial';
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const startCheckout = async () => {
    setBusy('checkout');
    try {
      const { data } = await API.post('/locker/subscription/checkout', {
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error('Unable to start checkout');
        setBusy(null);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to start checkout';
      toast.error(msg);
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]" data-testid="paywall-loading">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#737373]">Loading...</p>
        </div>
      </div>
    );
  }

  const trialUsed = !!access?.trial_used;

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="locker-paywall">
      {/* Top bar */}
      <div className="bg-[#1A2E1D] text-white">
        <div className="container mx-auto px-6 md:px-10 py-4 max-w-6xl flex items-center justify-between">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors" data-testid="paywall-back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="text-sm text-white/80">
            Hi, {user?.full_name?.split(' ')[0] || 'Player'}
          </div>
        </div>
      </div>

      {/* Hero / Paywall */}
      <main className="relative">
        {/* Blurred preview behind the paywall */}
        <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden select-none">
          <div className="container mx-auto px-6 md:px-10 py-10 max-w-6xl filter blur-md opacity-30">
            <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-2">Player's Locker</p>
            <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A] mb-3">Your Mental Performance</h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10 mt-8">
              {[Brain, Heart, ArrowRight].map((Icon, i) => (
                <div key={i} className="bg-white border border-[#E7E5E4] p-6">
                  <span className="w-10 h-10 rounded-full bg-[#1A2E1D] text-white flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5" />
                  </span>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#737373] mb-1">Metric</p>
                  <p className="text-4xl font-['Playfair_Display'] text-[#1A1A1A] mb-1">7.4<span className="text-base text-[#737373]"> / 10</span></p>
                  <div className="h-2 bg-[#E7E5E4] mt-4"></div>
                </div>
              ))}
            </div>
            <div className="bg-white border border-[#E7E5E4] p-6 h-48"></div>
          </div>
        </div>

        {/* Paywall card */}
        <div className="relative container mx-auto px-6 md:px-10 py-12 md:py-16 max-w-3xl">
          <div className="bg-white border border-[#E7E5E4] shadow-xl p-8 md:p-12">
            <div className="flex flex-col items-center text-center mb-8">
              <span className="w-14 h-14 rounded-full bg-[#1A2E1D] text-white flex items-center justify-center mb-4">
                <Lock className="w-7 h-7" />
              </span>
              <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-2">Player's Locker · Premium</p>
              <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A] mb-3">
                Train the mind. Track the gap.
              </h1>
              <p className="text-[#57534E] max-w-xl leading-relaxed">
                The Player's Locker is your personal performance tracker, reflection journal, and (soon) coach communication tool — built around the QMG Blueprint: Awareness · Acceptance · Action.
              </p>
              <div className="mt-5">
                <QMGSymbols size="sm" tone="dark" />
              </div>
            </div>

            {cancelled && (
              <div className="mb-6 bg-[#FFF4ED] border border-[#BC562B]/30 px-4 py-3 text-sm text-[#7A3617]" data-testid="paywall-cancelled-msg">
                Checkout was cancelled — no worries, you can try again anytime.
              </div>
            )}

            {/* Features list */}
            <ul className="space-y-3 mb-8" data-testid="paywall-features">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-[#1A1A1A]">
                  <span className="w-5 h-5 rounded-full bg-[#2C5530]/10 text-[#2C5530] flex items-center justify-center mt-0.5 flex-shrink-0">
                    <Check className="w-3 h-3" />
                  </span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            {/* CTAs */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Trial CTA */}
              <button
                onClick={startTrial}
                disabled={busy !== null || trialUsed}
                className={`flex flex-col items-start p-5 border transition-colors text-left ${
                  trialUsed
                    ? 'border-[#E7E5E4] bg-[#FAFAF9] cursor-not-allowed opacity-60'
                    : 'border-[#2C5530] bg-[#2C5530] text-white hover:bg-[#1E3A21]'
                }`}
                data-testid="paywall-start-trial-btn"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className={`w-4 h-4 ${trialUsed ? 'text-[#737373]' : 'text-white'}`} />
                  <span className={`text-xs uppercase tracking-[0.2em] font-semibold ${trialUsed ? 'text-[#737373]' : 'text-white/80'}`}>
                    {trialUsed ? 'Trial used' : 'Free trial'}
                  </span>
                </div>
                <p className={`font-['Playfair_Display'] text-2xl mb-1 ${trialUsed ? 'text-[#737373]' : 'text-white'}`}>
                  7 days free
                </p>
                <p className={`text-sm ${trialUsed ? 'text-[#737373]' : 'text-white/80'}`}>
                  {trialUsed
                    ? 'You\'ve already used your free trial.'
                    : 'No card required. Full Locker access for 7 days.'}
                </p>
                {!trialUsed && (
                  <span className="mt-3 text-sm font-semibold inline-flex items-center gap-1">
                    {busy === 'trial' ? 'Starting…' : 'Start free trial'} <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </button>

              {/* Paid CTA */}
              <button
                onClick={startCheckout}
                disabled={busy !== null}
                className="flex flex-col items-start p-5 border border-[#1A2E1D] bg-white hover:bg-[#FAFAF9] transition-colors text-left disabled:opacity-60"
                data-testid="paywall-subscribe-btn"
              >
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-[#BC562B]" />
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#737373]">Yearly plan</span>
                </div>
                <p className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-1">
                  $99<span className="text-base text-[#737373]"> / year</span>
                </p>
                <p className="text-sm text-[#57534E]">
                  Billed annually. Cancel anytime. Locks in your access for 12 months.
                </p>
                <span className="mt-3 text-sm font-semibold inline-flex items-center gap-1 text-[#1A2E1D]">
                  {busy === 'checkout' ? 'Redirecting…' : 'Subscribe now'} <ArrowRight className="w-4 h-4" />
                </span>
              </button>
            </div>

            <p className="mt-8 text-xs text-[#737373] text-center">
              Educational content (QMG Blueprint, Mind Factor, Acceptance vs. Resistance) remains free with your account.
            </p>
          </div>

          {/* Footer logout link */}
          <div className="mt-6 text-center">
            <Link to="/dashboard" className="text-sm text-[#737373] hover:text-[#1A1A1A] inline-flex items-center gap-1" data-testid="paywall-back-link">
              <Calendar className="w-3.5 h-3.5" /> Back to dashboard
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LockerPaywallPage;
